/**
 * ExtractedDocumentTable — renders the *extracted* (structured) view of a
 * document (invoice, PO, proof-of-delivery) as labelled fields + a line-item
 * table, rather than the raw scanned image.
 *
 * This is the building block for the 3-way match overlay and the per-line
 * "Document" column popout: AP review happens against the data we pulled out of
 * each document, so the comparison is field-to-field instead of eyeballing two
 * PDFs side by side.
 *
 * Presentational only — the caller supplies the already-extracted data.
 */

import {
  FileTextIcon,
  ReceiptIcon,
  TruckIcon,
  CheckCircleIcon,
  WarningIcon,
  InfoIcon,
  EnvelopeIcon,
  BrainIcon,
  type Icon,
} from '@phosphor-icons/react';
import { T } from 'gt-react';
import { cn } from '@/lib/utils';

/** Per-line status drives the row marker + colour. */
export type ExtractedLineStatus = 'match' | 'mismatch' | 'info';

export interface ExtractedDocLineItem {
  /**
   * The line's own line number on its source document. Rows are rendered in the
   * invoice's order across all three documents, so these stay with their line
   * (and may appear non-sequential once a column is reordered to align).
   */
  number?: string | number | null;
  description: string;
  /** Optional second line under the description (e.g. provenance note). */
  note?: string | null;
  quantity?: string | null;
  /** Middle column value (unit price, "cases shipped", …). */
  secondary?: string | null;
  /** Right column value (line amount, "received", …). */
  amount?: string | null;
  status?: ExtractedLineStatus | null;
}

export interface ExtractedDocField {
  label: string;
  value: string;
  /** Highlight a key field (e.g. the shared PO number). */
  emphasize?: boolean;
}

export type ExtractedDocKind =
  | 'invoice'
  | 'purchase_order'
  | 'proof_of_delivery';

export interface ExtractedDocument {
  kind: ExtractedDocKind;
  title: string;
  /** Who issued the document (vendor / carrier / buyer). */
  issuer?: string;
  /** Where the document came from — e.g. "a separate carrier email". */
  sourceNote?: string;
  /** AI reasoning for why this document was matched to the PO/invoice. */
  matchReason?: string;
  fields: ExtractedDocField[];
  /** Column headers; a column is omitted when its label is absent. */
  columns: { quantity?: string; secondary?: string; amount?: string };
  lineItems: ExtractedDocLineItem[];
}

const KIND_META: Record<ExtractedDocKind, { icon: Icon; tint: string }> = {
  invoice: { icon: ReceiptIcon, tint: 'text-teal-600' },
  purchase_order: { icon: FileTextIcon, tint: 'text-indigo-600' },
  proof_of_delivery: { icon: TruckIcon, tint: 'text-sky-600' },
};

function StatusMarker({ status }: { status?: ExtractedLineStatus | null }) {
  if (status === 'mismatch') {
    return <WarningIcon className="size-3.5 text-amber-500" weight="fill" />;
  }
  if (status === 'info') {
    return <InfoIcon className="size-3.5 text-blue-500" weight="fill" />;
  }
  if (status === 'match') {
    return (
      <CheckCircleIcon className="size-3.5 text-green-600" weight="fill" />
    );
  }
  return null;
}

export function ExtractedDocumentTable({ doc }: { doc: ExtractedDocument }) {
  const { icon: KindIcon, tint } = KIND_META[doc.kind];
  const { quantity, secondary, amount } = doc.columns;
  const hasNumbers = doc.lineItems.some((li) => li.number != null);

  return (
    <div className="flex h-full min-h-0 flex-col rounded-lg border bg-background">
      {/* Header */}
      <div className="border-b px-3 py-2">
        <div className="flex items-center gap-2">
          <KindIcon className={cn('size-4 shrink-0', tint)} weight="fill" />
          <div className="min-w-0">
            <div className="truncate text-sm font-semibold">{doc.title}</div>
            {doc.issuer && (
              <div className="truncate text-xs text-muted-foreground">
                {doc.issuer}
              </div>
            )}
          </div>
        </div>
        {doc.sourceNote && (
          <div className="mt-1.5 flex items-start gap-1.5 rounded bg-sky-50 px-2 py-1 text-[11px] text-sky-700">
            <EnvelopeIcon className="mt-0.5 size-3 shrink-0" />
            <span>{doc.sourceNote}</span>
          </div>
        )}
        {doc.matchReason && (
          <div className="mt-1.5 flex items-start gap-1.5 rounded bg-violet-50 px-2 py-1 text-[11px] text-violet-700">
            <BrainIcon className="mt-0.5 size-3 shrink-0" weight="fill" />
            <span>
              <span className="font-medium">
                <T context="prefix for an AI-generated reason a document matched">
                  Why matched:
                </T>
              </span>{' '}
              {doc.matchReason}
            </span>
          </div>
        )}
      </div>

      {/* Extracted fields */}
      {doc.fields.length > 0 && (
        <dl className="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1 border-b px-3 py-2 text-xs">
          {doc.fields.map((f) => (
            <div key={f.label} className="contents">
              <dt className="text-muted-foreground">{f.label}</dt>
              <dd
                className={cn(
                  'break-words text-right',
                  f.emphasize && 'font-semibold text-foreground',
                )}
              >
                {f.value}
              </dd>
            </div>
          ))}
        </dl>
      )}

      {/* Extracted line items — rendered in the invoice's order; the leading #
          is each line's own source line number (so it can read non-sequential
          once the column is reordered to align with the invoice). */}
      <div className="min-h-0 flex-1 overflow-y-auto">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-muted/60 backdrop-blur">
            <tr className="text-left text-[10px] uppercase tracking-wide text-muted-foreground">
              {hasNumbers && (
                <th className="w-8 px-2 py-1.5 text-center font-medium">
                  <T context="line item number column header">#</T>
                </th>
              )}
              <th className="px-3 py-1.5 font-medium">
                <T>Item</T>
              </th>
              {quantity && (
                <th className="px-2 py-1.5 text-right font-medium">
                  {quantity}
                </th>
              )}
              {secondary && (
                <th className="px-2 py-1.5 text-right font-medium">
                  {secondary}
                </th>
              )}
              {amount && (
                <th className="px-3 py-1.5 text-right font-medium">{amount}</th>
              )}
            </tr>
          </thead>
          <tbody>
            {doc.lineItems.map((li, i) => (
              <tr key={`${li.description}-${i}`} className="border-t align-top">
                {hasNumbers && (
                  <td className="px-2 py-1.5 text-center font-medium tabular-nums text-muted-foreground">
                    {li.number ?? '—'}
                  </td>
                )}
                <td className="px-3 py-1.5">
                  <div className="flex items-start gap-1.5">
                    <span className="mt-0.5">
                      <StatusMarker status={li.status} />
                    </span>
                    <div className="min-w-0">
                      <div className="font-medium">{li.description}</div>
                      {li.note && (
                        <div className="text-[11px] text-muted-foreground">
                          {li.note}
                        </div>
                      )}
                    </div>
                  </div>
                </td>
                {quantity && (
                  <td className="px-2 py-1.5 text-right tabular-nums">
                    {li.quantity ?? '—'}
                  </td>
                )}
                {secondary && (
                  <td className="px-2 py-1.5 text-right tabular-nums">
                    {li.secondary ?? '—'}
                  </td>
                )}
                {amount && (
                  <td className="px-3 py-1.5 text-right font-medium tabular-nums">
                    {li.amount ?? '—'}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
